import { Row } from '@tanstack/react-table';
import { UpstreamEvent } from 'client';

import { Badge } from 'DesignSystem/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'DesignSystem/ui/tooltip';

type Props = {
  row: Row<UpstreamEvent>;
};

enum EventSeverity {
  LOW = 0,
  MODERATE = 1,
  MAJOR = 2,
  CRITICAL = 3,
}

const EVENT_SEVERITY_EXPLANATION = {
  [EventSeverity.LOW]:
    'Potential risk only, forward-looking plans, resolved issues, or financial announcements without immediate supply impact',
  [EventSeverity.MODERATE]:
    'Class III recall; regional shortage; warning letter that could escalate; price increase 2-10%',
  [EventSeverity.MAJOR]:
    'Class II recall; FDA shortage list; multi-state hospital impact; >10% price spike or prolonged rationing',
  [EventSeverity.CRITICAL]:
    'Class I recall; national shortage of life-support products; >50% U.S. supply lost; any event causing deaths or surgery cancellations',
};

const EVENT_SEVERITY_NAME = {
  [EventSeverity.LOW]: 'Low',
  [EventSeverity.MODERATE]: 'Moderate',
  [EventSeverity.MAJOR]: 'Major',
  [EventSeverity.CRITICAL]: 'Critical',
};

export function Severity({ row }: Props) {
  const severity = row.original.severity;

  switch (severity) {
    case EventSeverity.LOW:
      return (
        <BadgeWithTooltip severity={severity}>
          <Badge variant="secondary" className="cursor-default">
            Low
          </Badge>
        </BadgeWithTooltip>
      );
    case EventSeverity.MODERATE:
      return (
        <BadgeWithTooltip severity={severity}>
          <Badge variant="yellow" className="cursor-default text-red-950">
            Moderate
          </Badge>
        </BadgeWithTooltip>
      );
    case EventSeverity.MAJOR:
      return (
        <BadgeWithTooltip severity={severity}>
          <Badge variant="warningMuted" className="cursor-default">
            Major
          </Badge>
        </BadgeWithTooltip>
      );
    case EventSeverity.CRITICAL:
      return (
        <BadgeWithTooltip severity={severity}>
          <Badge variant="destructiveMuted" className="cursor-default">
            Critical
          </Badge>
        </BadgeWithTooltip>
      );
    default:
      return <>–</>;
  }
}

function BadgeWithTooltip({ children, severity }: { children: React.ReactNode; severity: EventSeverity }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent className="flex w-[300px] flex-col gap-1 text-wrap p-2">
          <div className="type-body-xs pl-1 pt-1 font-medium">{EVENT_SEVERITY_NAME[severity]}</div>
          <div className="type-body-2xs pl-1 font-medium leading-normal">{EVENT_SEVERITY_EXPLANATION[severity]}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
